// Projektauswahl in der Kopfzeile: listet Projekte, wählt das aktive und legt neue an.
//
// Beim Wechsel des aktiven Projekts lädt die App-Shell (App.tsx) Katalog + Graph nach; hier
// wird nur die Auswahl gehalten und ein neues Projekt über POST /projects angelegt.

import { useCallback, useState, type JSX } from 'react';
import { useI18n } from '../i18n';
import { useStore } from '../state/store';
import { createProject, listProjects } from '../api/client';
import { Button, LabeledInput, LabeledSelect } from './ui';

// Aus einem Anzeigenamen eine kebab-case-Projekt-ID ableiten (deckt sich mit dem
// Backend-Muster [A-Za-z0-9][A-Za-z0-9._-]*). Umlaute werden transliteriert — hier gefahrlos,
// weil es eine technische ID ist, keine sichtbare Beschriftung.
function toId(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

export function ProjectPicker(): JSX.Element {
  const { t } = useI18n();
  const { projects, setProjects, activeProjectId, setActiveProjectId, notify, reportError } =
    useStore();

  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDomain, setNewDomain] = useState('biogas');
  const [busy, setBusy] = useState(false);

  const submit = useCallback(async () => {
    const name = newName.trim();
    if (!name) return;
    const id = toId(name);
    if (!id) return;
    setBusy(true);
    try {
      await createProject({ id, name, domain: newDomain });
      const list = await listProjects();
      setProjects(list.projects);
      setActiveProjectId(id);
      setCreating(false);
      setNewName('');
      notify('success', t('project.created', { name }));
    } catch (err) {
      reportError(err, t('project.createFailed'));
    } finally {
      setBusy(false);
    }
  }, [newName, newDomain, setProjects, setActiveProjectId, notify, reportError, t]);

  return (
    <div className="flex items-center gap-2">
      <label className="flex items-center gap-2">
        <span className="text-xs text-bf-muted">{t('project.label')}</span>
        <select
          aria-label={t('project.label')}
          data-testid="project-select"
          value={activeProjectId ?? ''}
          onChange={(e) => setActiveProjectId(e.target.value || null)}
          className="min-w-44 rounded border border-bf-border bg-bf-navy px-2 py-1 text-sm text-bf-white outline-none focus:border-bf-gold"
        >
          <option value="">{t('project.none')}</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </label>

      <span data-testid="project-new-toggle">
        <Button onClick={() => setCreating((v) => !v)} title={t('project.new')}>
          + {t('project.new')}
        </Button>
      </span>

      {creating && (
        <div
          data-testid="project-new-form"
          className="absolute right-4 top-14 z-40 w-72 rounded border border-bf-border bg-bf-navy-light p-4 shadow-xl"
        >
          <h4 className="mb-3 text-sm font-semibold text-bf-white">{t('project.newTitle')}</h4>
          <div className="flex flex-col gap-3">
            <span data-testid="project-new-name">
              <LabeledInput
                label={t('project.name')}
                value={newName}
                onChange={setNewName}
                placeholder={t('project.namePlaceholder')}
              />
            </span>
            <LabeledSelect label={t('project.domain')} value={newDomain} onChange={setNewDomain}>
              <option value="biogas">{t('domain.biogas')}</option>
              <option value="abwasser">{t('domain.abwasser')}</option>
              <option value="mhkw">{t('domain.mhkw')}</option>
              <option value="brauerei">{t('domain.brauerei')}</option>
              <option value="molkerei">{t('domain.molkerei')}</option>
              <option value="baeder">{t('domain.baeder')}</option>
              <option value="fernwaerme">{t('domain.fernwaerme')}</option>
              <option value="wasserwerk">{t('domain.wasserwerk')}</option>
              <option value="oberflaeche">{t('domain.oberflaeche')}</option>
            </LabeledSelect>
            {newName.trim() && (
              <p className="text-xs text-bf-muted">
                {t('project.idPreview')}: <span className="text-bf-white">{toId(newName)}</span>
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button onClick={() => setCreating(false)}>{t('common.cancel')}</Button>
              <span data-testid="project-new-submit">
                <Button variant="primary" onClick={submit} disabled={busy || !newName.trim()}>
                  {t('common.create')}
                </Button>
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
